import Service from 'react-reservice';

import {
  IPelicanCopyTradeBroker,
  IPelicanCopyTradeCopierInfo,
  IPelicanCopyTradeCopySettings,
  IPelicanCopyTradeServer,
  IPelicanCopyTradeSignalClosedColumns,
  IPelicanCopyTradeSignalOpenColumns,
  IPelicanCopyTradeStrategyDetailData,
  IPelicanCopyTradeUserInfo,
  IStrategy
} from '@aqx-core/common/api/copy-trade/model';
import {
  ECopyTradeFilter,
  ECopyTradeTimeframe,
  ESectionPreferenceStrategyType
} from '@aqx-core/common/constants/copy-trade';

export interface ICopyTradeContext {
  authLoading: boolean;
  strategyListLoading: boolean;
  strategyListLoadingMore: boolean;
  codeVerifier: string;
  codeChallenge: string;
  isAuthenticated: boolean;
  strategyList: IStrategy[];
  selectedStrategySection: ECopyTradeFilter;
  displayStrategyList: IStrategy[];
  selectedStrategy: IStrategy | null;
  selectedStrategyLoading: boolean;
  selectedStrategyDetail: IPelicanCopyTradeStrategyDetailData | null;
  selectedStrategyDetailLoading: boolean;
  searchQuery: string;
  selectedStrategyDetailTab: ESectionPreferenceStrategyType;
  userInfoLoading: boolean;
  userInfo: IPelicanCopyTradeUserInfo | null;
  strategyImage: Blob | undefined;
  hasMoreStrategies: boolean;
  isLoadingCopyTradeOpenPositions: boolean;
  isLoadingCopyTradeHistory: boolean;
  isLoadingCopyTradeHistoryMore: boolean;
  hasMoreCopyTradeHistory: boolean;
  copyTradeOpenPositions: IPelicanCopyTradeSignalOpenColumns[];
  copyTradeHistory: IPelicanCopyTradeSignalClosedColumns[];
  // Master data controller
  brokers: IPelicanCopyTradeBroker[] | null;
  servers: IPelicanCopyTradeServer[] | null;
  isLoadingServers: boolean;
  // Copier
  selectedCopierId: string | undefined;
  shouldShowLinkPelicanAccountModal: boolean;
  copierInfo: IPelicanCopyTradeCopierInfo | null;
  isPrecheckingCopyStrategy: boolean;
  isPrecheckingEditCopyStrategy: boolean;
  isPrecheckingStopCopying: boolean;
  isPrecheckingDrawdown: boolean;
  isLoadingResetCopierDrawdown: boolean;
  isLoadingEditCopierDrawdown: boolean;
  isLoadingRemoveCopier: boolean;
  isLoadingLinkAccountToPelicanCopyTrade: boolean;
  isLoadingSwitchCopier: boolean;
  // Copy settings
  copySettings: IPelicanCopyTradeCopySettings | null;
  isLoadingCopySettings: boolean;
  isLoadingCreateCopySettings: boolean;
  isLoadingEditCopySettings: boolean;
  isLoadingStopCopySettings: boolean;
  // Return history chart
  returnHistoryChartTimeframe: ECopyTradeTimeframe;
  isReturnHistoryChartExpanded: boolean;
}

const defaultContext: ICopyTradeContext = {
  authLoading: false,
  strategyListLoading: false,
  strategyListLoadingMore: false,
  codeVerifier: '',
  codeChallenge: '',
  isAuthenticated: false,
  strategyList: [],
  selectedStrategySection: ECopyTradeFilter.ALL,
  displayStrategyList: [],
  selectedStrategy: null,
  selectedStrategyLoading: false,
  selectedStrategyDetail: null,
  selectedStrategyDetailLoading: false,
  searchQuery: '',
  selectedStrategyDetailTab: ESectionPreferenceStrategyType.STRATEGY_PROFILE,
  userInfoLoading: false,
  userInfo: null,
  strategyImage: undefined,
  hasMoreStrategies: false,
  isLoadingCopyTradeOpenPositions: false,
  isLoadingCopyTradeHistory: false,
  isLoadingCopyTradeHistoryMore: false,
  hasMoreCopyTradeHistory: false,
  copyTradeOpenPositions: [],
  copyTradeHistory: [],
  // Master data controller
  brokers: null,
  servers: null,
  isLoadingServers: false,
  // Copier
  selectedCopierId: undefined,
  shouldShowLinkPelicanAccountModal: false,
  copierInfo: null,
  isPrecheckingCopyStrategy: false,
  isPrecheckingEditCopyStrategy: false,
  isPrecheckingStopCopying: false,
  isPrecheckingDrawdown: false,
  isLoadingResetCopierDrawdown: false,
  isLoadingEditCopierDrawdown: false,
  isLoadingRemoveCopier: false,
  isLoadingLinkAccountToPelicanCopyTrade: false,
  isLoadingSwitchCopier: false,
  // Copy settings
  copySettings: null,
  isLoadingCopySettings: false,
  isLoadingCreateCopySettings: false,
  isLoadingEditCopySettings: false,
  isLoadingStopCopySettings: false,
  // Return history chart
  returnHistoryChartTimeframe: ECopyTradeTimeframe.DAILY,
  isReturnHistoryChartExpanded: false
};

class CopyTradeService extends Service<ICopyTradeContext> {
  constructor() {
    super();
    this.context = { ...defaultContext };
  }

  setAuthLoading = (loading: boolean) => {
    this.context.authLoading = loading;
  };

  setStrategyListLoading = (loading: boolean) => {
    this.context.strategyListLoading = loading;
  };

  setStrategyListLoadingMore = (loading: boolean) => {
    this.context.strategyListLoadingMore = loading;
  };

  setCodeVerifier = (codeVerifier: string) => {
    this.context.codeVerifier = codeVerifier;
  };

  setIsAuthenticated = (isAuthenticated: boolean) => {
    this.context.isAuthenticated = isAuthenticated;
  };

  setStrategyList = (strategyList: IStrategy[]) => {
    this.context.strategyList = strategyList;
  };

  setSelectedStrategySection = (selectedStrategySection: ECopyTradeFilter) => {
    this.context.selectedStrategySection = selectedStrategySection;
  };

  setDisplayStrategyList = (displayStrategyList: IStrategy[]) => {
    this.context.displayStrategyList = displayStrategyList;
  };

  setSelectedStrategy = (selectedStrategy: IStrategy | null) => {
    this.context.selectedStrategy = selectedStrategy;
  };

  setSelectedStrategyLoading = (selectedStrategyLoading: boolean) => {
    this.context.selectedStrategyLoading = selectedStrategyLoading;
  };

  setSelectedStrategyDetail = (selectedStrategyDetail: IPelicanCopyTradeStrategyDetailData | null) => {
    this.context.selectedStrategyDetail = selectedStrategyDetail;
  };

  setSelectedStrategyDetailLoading = (selectedStrategyDetailLoading: boolean) => {
    this.context.selectedStrategyDetailLoading = selectedStrategyDetailLoading;
  };

  setSearchQuery = (searchQuery: string) => {
    this.context.searchQuery = searchQuery;
  };

  setSelectedStrategyDetailTab = (selectedStrategyDetailTab: ESectionPreferenceStrategyType) => {
    this.context.selectedStrategyDetailTab = selectedStrategyDetailTab;
  };

  setUserInfoLoading = (userInfoLoading: boolean) => {
    this.context.userInfoLoading = userInfoLoading;
  };

  setUserInfo = (userInfo: IPelicanCopyTradeUserInfo | null) => {
    this.context.userInfo = userInfo;
  };

  setStrategyImage = (strategyImage: Blob | undefined) => {
    this.context.strategyImage = strategyImage;
  };

  setHasMoreStrategies = (hasMore: boolean) => {
    this.context.hasMoreStrategies = hasMore;
  };

  setIsLoadingCopyTradeOpenPositions = (isLoadingCopyTradeOpenPositions: boolean) => {
    this.context.isLoadingCopyTradeOpenPositions = isLoadingCopyTradeOpenPositions;
  };

  setIsLoadingCopyTradeHistory = (isLoadingCopyTradeHistory: boolean) => {
    this.context.isLoadingCopyTradeHistory = isLoadingCopyTradeHistory;
  };

  setCopyTradeOpenPositions = (copyTradeOpenPositions: IPelicanCopyTradeSignalOpenColumns[]) => {
    this.context.copyTradeOpenPositions = copyTradeOpenPositions;
  };

  setCopyTradeHistory = (copyTradeHistory: IPelicanCopyTradeSignalClosedColumns[]) => {
    this.context.copyTradeHistory = copyTradeHistory;
  };

  setIsLoadingCopyTradeHistoryMore = (isLoadingCopyTradeHistoryMore: boolean) => {
    this.context.isLoadingCopyTradeHistoryMore = isLoadingCopyTradeHistoryMore;
  };

  setHasMoreCopyTradeHistory = (hasMoreCopyTradeHistory: boolean) => {
    this.context.hasMoreCopyTradeHistory = hasMoreCopyTradeHistory;
  };

  // Master data controller
  setBrokers = (brokers: IPelicanCopyTradeBroker[] | null) => {
    this.context.brokers = brokers;
  };

  setServers = (servers: IPelicanCopyTradeServer[] | null) => {
    this.context.servers = servers;
  };

  setIsLoadingServers = (isLoadingServers: boolean) => {
    this.context.isLoadingServers = isLoadingServers;
  };

  setSelectedCopierId = (selectedCopierId?: string) => {
    this.context.selectedCopierId = selectedCopierId;
  };

  setShouldShowLinkPelicanAccountModal = (shouldShow: boolean) => {
    this.context.shouldShowLinkPelicanAccountModal = shouldShow;
  };

  setCopierInfo = (copierInfo: IPelicanCopyTradeCopierInfo | null) => {
    this.context.copierInfo = copierInfo;
  };

  setIsPrecheckingCopyStrategy = (isPrecheckingCopyStrategy: boolean) => {
    this.context.isPrecheckingCopyStrategy = isPrecheckingCopyStrategy;
  };

  setIsPrecheckingEditCopyStrategy = (isPrecheckingEditCopyStrategy: boolean) => {
    this.context.isPrecheckingEditCopyStrategy = isPrecheckingEditCopyStrategy;
  };

  setIsPrecheckingStopCopying = (isPrecheckingStopCopying: boolean) => {
    this.context.isPrecheckingStopCopying = isPrecheckingStopCopying;
  };

  setIsPrecheckingDrawdown = (isPrecheckingDrawdown: boolean) => {
    this.context.isPrecheckingDrawdown = isPrecheckingDrawdown;
  };

  setIsLoadingRemoveCopier = (isLoadingRemoveCopier: boolean) => {
    this.context.isLoadingRemoveCopier = isLoadingRemoveCopier;
  };

  setIsLoadingResetCopierDrawdown = (isLoadingResetCopierDrawdown: boolean) => {
    this.context.isLoadingResetCopierDrawdown = isLoadingResetCopierDrawdown;
  };

  setIsLoadingEditCopierDrawdown = (isLoadingEditCopierDrawdown: boolean) => {
    this.context.isLoadingEditCopierDrawdown = isLoadingEditCopierDrawdown;
  };

  setIsLoadingLinkAccountToPelicanCopyTrade = (isLoadingLinkAccountToPelicanCopyTrade: boolean) => {
    this.context.isLoadingLinkAccountToPelicanCopyTrade = isLoadingLinkAccountToPelicanCopyTrade;
  };

  setIsLoadingSwitchCopier = (isLoadingSwitchCopier: boolean) => {
    this.context.isLoadingSwitchCopier = isLoadingSwitchCopier;
  };

  setCopySettings = (copySettings: IPelicanCopyTradeCopySettings | null) => {
    this.context.copySettings = copySettings;
  };

  setIsLoadingCopySettings = (isLoadingCopySettings: boolean) => {
    this.context.isLoadingCopySettings = isLoadingCopySettings;
  };

  setIsLoadingCreateCopySettings = (isLoadingCreateCopySettings: boolean) => {
    this.context.isLoadingCreateCopySettings = isLoadingCreateCopySettings;
  };

  setIsLoadingEditCopySettings = (isLoadingEditCopySettings: boolean) => {
    this.context.isLoadingEditCopySettings = isLoadingEditCopySettings;
  };

  setIsLoadingStopCopySettings = (isLoadingStopCopySettings: boolean) => {
    this.context.isLoadingStopCopySettings = isLoadingStopCopySettings;
  };

  setReturnHistoryChartTimeframe = (timeframe: ECopyTradeTimeframe) => {
    this.context.returnHistoryChartTimeframe = timeframe;
  };

  setIsReturnHistoryChartExpanded = (isExpanded: boolean) => {
    this.context.isReturnHistoryChartExpanded = isExpanded;
  };

  resetCopySettings = () => {
    this.context.copySettings = null;
    this.context.isLoadingCopySettings = false;
    this.context.isLoadingCreateCopySettings = false;
    this.context.isLoadingEditCopySettings = false;
    this.context.isLoadingStopCopySettings = false;
  };

  reset = () => {
    this.context = { ...defaultContext };
  };
}

export const copyTradeService = new CopyTradeService();
